import { useEffect, useMemo, useState, Fragment } from "react";
import React from "react";
import api from "@/lib/api";
import { downloadXlsx } from "@/lib/download";
import { Trophy, Info, ChevronDown, ChevronRight, Settings, Lock, Unlock, Download, FileText, Save, Trash2, Search, Plus, X, RotateCcw, Loader2, Users, TrendingDown, TrendingUp, Coins } from "lucide-react";
import { toast } from "sonner";
import { useTableSort, SortTh } from "@/lib/sortable";
import { IncentivesEmployeeTable } from "@/components/employees/EmployeeIncentivesSummary";

const fmtEur = (n) =>
  Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const fmtNum2 = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 });

const STATUS_STYLES = {
  BASELINE: { label: "BASELINE", cls: "bg-slate-100 text-slate-600" },
  OK: { label: "PREMIO", cls: "bg-emerald-100 text-emerald-700" },
  SIN_CRECIMIENTO: { label: "SIN CRECIMIENTO", cls: "bg-slate-50 text-slate-500" },
  NO_SE_COBRA: { label: "NO SE COBRA", cls: "bg-rose-100 text-rose-700" },
};

function MonthEmployeeSummary({ month }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [openEmp, setOpenEmp] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || state.data || state.loading) return;
    setState({ loading: true, data: null, error: null });
    try {
      const r = await api.get(`/incentives/month/${month.id}/employees`);
      setState({ loading: false, data: r.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e?.response?.data?.detail || "Error al cargar" });
    }
  };

  const d = state.data;
  return (
    <div className="border-b border-slate-200 bg-slate-50/60" data-testid={`month-employees-${month.id}`}>
      <button
        onClick={toggle}
        className="w-full px-4 py-2.5 flex items-center gap-2 text-left hover:bg-slate-100 transition-colors"
        data-testid={`month-employees-toggle-${month.id}`}
      >
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <Users className="h-4 w-4 text-indigo-600" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
          Resumen por empleado en las moléculas a incentivar
        </span>
        {d && (
          <span className="text-[11px] text-slate-500 font-mono-num ml-1">
            media farmacia {fmtNum2(d.totals.team_gen_pct)}%
            {d.totals.prev_team_gen_pct != null && ` · antes ${fmtNum2(d.totals.prev_team_gen_pct)}%`}
            {d.totals.n_below > 0 && (
              <span className="text-rose-600 font-bold"> · {d.totals.n_below} por debajo</span>
            )}
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {state.loading && (
            <div className="py-6 text-center text-slate-400 text-sm inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Calculando…
            </div>
          )}
          {state.error && <div className="py-4 text-rose-600 text-sm">{state.error}</div>}
          {d && !d.has_breakdown && (
            <div className="py-3 text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 text-xs">
              Este estudio no trae desglose por empleado, así que no puedo repartir el mérito.
            </div>
          )}
          {d && d.employees.length > 0 && (
            <>
              <p className="text-[11px] text-slate-500 mb-2">
                % de genérico de cada empleado medido <strong>solo en las {d.totals.n_molecules} moléculas a incentivar</strong>.
                En <span className="text-rose-600 font-bold">rojo</span> quien está {fmtNum2(d.below_threshold_pp)} puntos o más por
                debajo de la media de la farmacia ({fmtNum2(d.totals.team_gen_pct)}%).
                {d.prev_month && <> Comparado con <strong>{d.prev_month.label}</strong>.</>}
              </p>
              <IncentivesEmployeeTable data={d} testid={`month-employees-${month.id}`} />
            </>
          )}
          {d && d.has_breakdown && d.employees.length === 0 && (
            <div className="py-3 text-slate-400 text-sm">Sin dispensaciones de estas moléculas por empleado.</div>
          )}
        </div>
      )}
    </div>
  );
}

function MonthIndividualSplit({ month }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, data: null, error: null });

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || state.data || state.loading) return;
    setState({ loading: true, data: null, error: null });
    try {
      const r = await api.get(`/incentives/month/${month.id}/split`);
      setState({ loading: false, data: r.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e?.response?.data?.detail || "Error al calcular el reparto" });
    }
  };

  const d = state.data;
  return (
    <div className="border-b border-slate-200 bg-amber-50/50" data-testid={`month-split-${month.id}`}>
      <button
        onClick={toggle}
        className="w-full px-4 py-2.5 flex items-center gap-2 text-left hover:bg-amber-100/60 transition-colors"
        data-testid={`month-split-toggle-${month.id}`}
      >
        {open ? <ChevronDown className="h-4 w-4 text-amber-500" /> : <ChevronRight className="h-4 w-4 text-amber-500" />}
        <Coins className="h-4 w-4 text-amber-600" />
        <span className="text-xs font-bold uppercase tracking-wider text-amber-900">
          ¿Te gustaría ver el reparto si fuera individual?
        </span>
        {d && (
          <span className="text-[11px] text-amber-800 font-mono-num ml-1">
            {fmtEur(d.total_repartido_eur)} repartidos entre {d.employees.filter((e) => e.premio_eur > 0).length} personas
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {state.loading && (
            <div className="py-6 text-slate-400 text-sm inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Calculando el reparto…
            </div>
          )}
          {state.error && <div className="py-4 text-rose-600 text-sm">{state.error}</div>}
          {d && (
            <>
              <p className="text-[11px] text-slate-600 mb-2">
                Simulación: el premio de cada molécula se reparte según <strong>cuánto ha crecido cada empleado en genérico</strong> en
                esa molécula respecto a {d.baseline.label} (uds/mes). Si en una molécula premiada nadie creció, se reparte a prorrata de
                las unidades de genérico que ha hecho cada uno.
                {d.sin_repartir_eur > 0.01 && (
                  <> Quedan <strong>{fmtEur(d.sin_repartir_eur)}</strong> sin asignar por falta de desglose.</>
                )}
              </p>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid={`month-split-table-${month.id}`}>
                  <thead className="bg-amber-600 text-white text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Empleado</th>
                      <th className="text-right px-3 py-2">Le tocaría</th>
                      <th className="text-right px-3 py-2">% del premio</th>
                      <th className="text-right px-3 py-2">Crecimiento uds/mes</th>
                      <th className="text-right px-3 py-2">Uds genérico</th>
                      <th className="text-right px-3 py-2">Moléculas con premio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.employees.map((e, i) => (
                      <tr key={e.id} className={e.premio_eur > 0 ? "hover:bg-amber-50" : "text-slate-400"} data-testid={`month-split-row-${e.id}`}>
                        <td className="px-3 py-2 text-[10px] font-bold text-slate-400 font-mono-num">{i + 1}</td>
                        <td className="px-3 py-2 font-semibold">{e.name}</td>
                        <td className={`px-3 py-2 text-right font-mono-num font-bold ${e.premio_eur > 0 ? "text-emerald-700" : ""}`}>
                          {fmtEur(e.premio_eur)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-num">{fmtNum2(e.share_pct)}%</td>
                        <td className="px-3 py-2 text-right font-mono-num">{fmtNum2(e.growth_units_pm)}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{e.gen_units.toLocaleString("es-ES")}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{e.molecules}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 text-xs font-bold">
                    <tr>
                      <td className="px-3 py-2" colSpan={2}>TOTAL DEL MES</td>
                      <td className="px-3 py-2 text-right font-mono-num">{fmtEur(d.month.total_premio_eur)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">100%</td>
                      <td className="px-3 py-2" colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">
                Es solo una simulación para ayudarte a decidir: el premio del mes no cambia.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, title }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.SIN_CRECIMIENTO;
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.cls} ${title ? "cursor-help" : ""}`}
    >
      {s.label}
    </span>
  );
}

function RatesCard({ rates, onEdit }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Tarifas por cambio</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Cantidades y rangos PVL totalmente parametrizables</div>
        </div>
        {onEdit && (
          <button
            onClick={onEdit}
            data-testid="open-rates-editor"
            title="Cambia las cantidades (€/cambio) y los rangos de PVL a tu gusto"
            className="inline-flex items-center gap-1.5 text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-md shadow-sm transition-colors whitespace-nowrap"
          >
            <Settings className="h-3.5 w-3.5" /> Editar tarifas
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="bg-slate-50 rounded-md p-3 text-center">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">PVL ≥{rates.tier1_min}€ y &lt;{rates.tier1_max}€</div>
          <div className="font-display text-2xl font-bold">{rates.tier1}€</div>
          <div className="text-[10px] text-slate-400">por cambio</div>
        </div>
        <div className="bg-slate-50 rounded-md p-3 text-center">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">PVL ≥{rates.tier2_min}€ y &lt;{rates.tier2_max}€</div>
          <div className="font-display text-2xl font-bold">{rates.tier2}€</div>
          <div className="text-[10px] text-slate-400">por cambio</div>
        </div>
        <div className="bg-slate-50 rounded-md p-3 text-center">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">PVL ≥{rates.tier3_min}€</div>
          <div className="font-display text-2xl font-bold">{rates.tier3}€</div>
          <div className="text-[10px] text-slate-400">por cambio</div>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-3">
        Crecimiento = u_genérico/mes (mes M) − u_genérico/mes (baseline). Sólo cuenta si {">"} 0. Si las unidades totales del mes caen por debajo del {rates.drop_threshold_pct ?? 90}% de la <strong>media de los 3 meses anteriores</strong> de ese conjunto homogéneo (pérdida de clientes), se marca <strong>NO SE COBRA</strong>. Así un mes atípico (agosto, Navidad) no penaliza.
      </p>
    </div>
  );
}

function RatesEditor({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    tier1_eur: initial.tier1, tier1_min: initial.tier1_min, tier1_max: initial.tier1_max,
    tier2_eur: initial.tier2, tier2_max: initial.tier2_max,
    tier3_eur: initial.tier3, tier3_min: initial.tier3_min,
    drop_threshold_pct: initial.drop_threshold_pct ?? 90,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: parseFloat(e.target.value) || 0 });

  const submit = async () => {
    setSaving(true);
    try {
      await api.put("/incentives/config", form);
      toast.success("Tarifas guardadas");
      onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar tarifas");
    } finally {
      setSaving(false);
    }
  };

  const F = ({ k, label, suffix }) => (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="flex items-center gap-1.5">
        <input
          data-testid={`rate-${k}`}
          type="number"
          step="0.5"
          value={form[k]}
          onChange={set(k)}
          className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm font-mono-num"
        />
        <span className="text-xs text-slate-400">{suffix}</span>
      </div>
    </label>
  );

  return (
    <div className="bg-amber-50/40 border-2 border-amber-200 rounded-lg p-5 mb-4" data-testid="rates-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-lg font-bold text-slate-900">Editar tarifas y umbral</div>
        <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-900">Cerrar</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <F k="tier1_min" label="Tier 1 mín. PVL" suffix="€" />
        <F k="tier1_max" label="Tier 1 máx. PVL" suffix="€" />
        <F k="tier1_eur" label="Tier 1 premio" suffix="€/cambio" />
        <div />
        <F k="tier2_max" label="Tier 2 máx. PVL" suffix="€" />
        <F k="tier2_eur" label="Tier 2 premio" suffix="€/cambio" />
        <F k="tier3_min" label="Tier 3 mín. PVL" suffix="€" />
        <F k="tier3_eur" label="Tier 3 premio" suffix="€/cambio" />
        <F k="drop_threshold_pct" label="Umbral NO SE COBRA" suffix="% de la media 3 meses" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="text-xs px-3 py-2 border border-slate-200 rounded-md bg-white hover:bg-slate-50">Cancelar</button>
        <button
          onClick={submit}
          disabled={saving}
          data-testid="save-rates"
          className="inline-flex items-center gap-1.5 bg-slate-950 text-white text-xs px-3 py-2 rounded-md font-medium hover:bg-slate-800 disabled:opacity-50"
        >
          <Save className="h-3 w-3" />
          Guardar tarifas
        </button>
      </div>
    </div>
  );
}

function MonthCard({ month, tracked, rates, isLockedBaseline, onLockBaseline }) {
  const [open, setOpen] = useState(false);
  const [expandedGh, setExpandedGh] = useState(null); // gh actualmente expandida (una a la vez)
  const [empCache, setEmpCache] = useState({});        // gh -> {loading, data, error}
  const [exportingFmt, setExportingFmt] = useState(null); // "xlsx" | "pdf" | null
  const isBaseline = month.is_baseline;
  const noPay = month.molecules.filter((m) => m.status === "NO_SE_COBRA").length;
  const okCount = month.molecules.filter((m) => m.status === "OK").length;
  const trackedMap = useMemo(() => Object.fromEntries(tracked.map((t) => [t.gh, t])), [tracked]);
  const sort = useTableSort(month.molecules || [], {
    vol_var: (m) => {
      const base = Number(m.volume_ref_u_pm || 0);
      const cur = Number(m.month_total_u_pm ?? ((m.month_marca_u_pm || 0) + (m.month_gen_u_pm || 0)));
      return base > 0 ? ((cur - base) / base) * 100 : 0;
    },
  });

  const toggleGh = async (gh) => {
    if (expandedGh === gh) {
      setExpandedGh(null);
      return;
    }
    setExpandedGh(gh);
    if (empCache[gh]?.data || empCache[gh]?.loading) return;
    setEmpCache((c) => ({ ...c, [gh]: { loading: true } }));
    try {
      const r = await api.get(`/dispensations/${month.id}/molecule-employees`, { params: { gh } });
      setEmpCache((c) => ({ ...c, [gh]: { loading: false, data: r.data } }));
    } catch (e) {
      setEmpCache((c) => ({ ...c, [gh]: { loading: false, error: e?.response?.data?.detail || "Error" } }));
    }
  };

  // Iter-87 · Exportación por mes (PDF/Excel) para imprimir al equipo
  const exportMonth = async (fmt, ev) => {
    ev?.stopPropagation?.();
    setExportingFmt(fmt);
    try {
      const res = await api.get(`/incentives/month/${month.id}/export`, {
        params: { format: fmt },
        responseType: "blob",
      });
      const mime = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `incentivos_${(month.label || month.id).replace(/[^a-zA-Z0-9]/g, "_")}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al exportar");
    } finally {
      setExportingFmt(null);
    }
  };

  return (
    <div className={`bg-white border rounded-lg overflow-hidden ${isBaseline ? (isLockedBaseline ? "border-slate-900 border-2" : "border-slate-300 border-dashed") : "border-slate-200"}`} data-testid={`incentives-month-${month.id}`}>
      <div className="flex items-stretch">
        <button
          onClick={() => setOpen(!open)}
          className="flex-1 p-5 hover:bg-slate-50 transition-colors text-left"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                  {month.label}
                  {isBaseline && (
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1 ${isLockedBaseline ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                      {isLockedBaseline && <Lock className="h-2.5 w-2.5" />}
                      BASELINE{isLockedBaseline ? " · FIJADO" : ""}
                    </span>
                  )}
                  {month.is_partial_month && (
                    <span
                      title={`Periodo de ${month.days_in_period} días (≈ ${(month.effective_months || 1).toFixed(2)} meses). Activa el prorrateo para comparar correctamente con el resto.`}
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1 bg-amber-100 text-amber-700 cursor-help"
                    >
                      ⚠ Periodo incompleto
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 font-mono-num">
                  {month.period_start} → {month.period_end} · {month.months} mes(es){month.days_in_period ? ` · ${month.days_in_period} días` : ""}
                </div>
              </div>
            </div>
            <div className="text-right">
              {isBaseline ? (
                <div className="text-xs text-slate-500">Mes de referencia</div>
              ) : (
                <>
                  <div className={`font-display text-3xl font-bold tracking-tighter ${month.total_premio_eur > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                    {fmtEur(month.total_premio_eur)}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono-num">
                    {okCount} con premio · {noPay} no se cobra
                  </div>
                </>
              )}
            </div>
          </div>
        </button>
        {!isBaseline && onLockBaseline && (
          <button
            onClick={onLockBaseline}
            data-testid={`set-baseline-${month.id}`}
            className="px-3 border-l border-slate-200 text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-colors"
            title="Fijar este mes como baseline"
          >
            <Lock className="h-4 w-4" />
          </button>
        )}
        {/* Iter-87 · Export por mes (PDF + Excel) para imprimir al equipo */}
        <div className="flex flex-col border-l border-slate-200">
          <button
            onClick={(ev) => exportMonth("pdf", ev)}
            disabled={!!exportingFmt}
            data-testid={`export-month-pdf-${month.id}`}
            className="flex-1 px-3 text-[10px] font-bold text-rose-600 hover:bg-rose-50 disabled:opacity-50 inline-flex items-center gap-1 border-b border-slate-200"
            title={`PDF · Incentivos ${month.label} para imprimir al equipo`}
          >
            {exportingFmt === "pdf" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
            PDF
          </button>
          <button
            onClick={(ev) => exportMonth("xlsx", ev)}
            disabled={!!exportingFmt}
            data-testid={`export-month-xlsx-${month.id}`}
            className="flex-1 px-3 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center gap-1"
            title={`Excel · Incentivos ${month.label} para el equipo`}
          >
            {exportingFmt === "xlsx" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Excel
          </button>
        </div>
      </div>

      {open && !isBaseline && <MonthEmployeeSummary month={month} />}
      {open && !isBaseline && (month.total_premio_eur || 0) > 0 && <MonthIndividualSplit month={month} />}

      {open && (
        <div className="border-t border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <SortTh field="gh" sort={sort} className="p-3 font-semibold">Molécula (G.H.)</SortTh>
                <SortTh field="pvl" sort={sort} align="right" className="p-3 font-semibold">PVL</SortTh>
                <SortTh field="rate" sort={sort} align="right" className="p-3 font-semibold">Tarifa</SortTh>
                <SortTh field="baseline_marca_u_pm" sort={sort} align="right" className="p-3 font-semibold">Marca u/mes (base)</SortTh>
                <SortTh field="baseline_gen_u_pm" sort={sort} align="right" className="p-3 font-semibold">Gen u/mes (base)</SortTh>
                <SortTh field="month_gen_u_pm" sort={sort} align="right" className="p-3 font-semibold">Gen u/mes (mes)</SortTh>
                <SortTh field="vol_var" sort={sort} align="right" className="p-3 font-semibold" title={`Total marca+gen u/mes del mes vs la media de los 3 meses anteriores. Si cae más de ${100 - (rates?.drop_threshold_pct ?? 90)}% se marca NO SE COBRA.`}>
                  Vol. total (vs media 3m)
                </SortTh>
                <SortTh field="growth_units" sort={sort} align="right" className="p-3 font-semibold">Crecimiento</SortTh>
                <SortTh field="premio_eur" sort={sort} align="right" className="p-3 font-semibold">Premio €</SortTh>
                <SortTh field="status" sort={sort} className="p-3 font-semibold">Estado</SortTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sort.sorted.map((m) => {
                const t = trackedMap[m.gh];
                const baseTotal = Number(m.volume_ref_u_pm || 0);
                const refMonths = Number(m.volume_ref_months || 0);
                const monthTotal = Number(m.month_total_u_pm ?? ((m.month_marca_u_pm || 0) + (m.month_gen_u_pm || 0)));
                const varPct = baseTotal > 0 ? ((monthTotal - baseTotal) / baseTotal) * 100 : 0;
                const dropLimit = -(100 - (rates?.drop_threshold_pct ?? 90)); // ej -10
                const isDrop = m.status === "NO_SE_COBRA";
                const isLightDrop = !isDrop && varPct < 0;
                const reasonNoCobra = isDrop
                  ? `NO SE COBRA: volumen total del mes ${fmtNum2(monthTotal)} u/mes < ${rates?.drop_threshold_pct ?? 90}% de la media de los ${refMonths} ${refMonths === 1 ? "mes" : "meses"} anteriores (${fmtNum2(baseTotal)} u/mes). Caída ${varPct.toFixed(1)}% (límite ${dropLimit.toFixed(0)}%). Se han perdido clientes en esta molécula.`
                  : undefined;
                const isExpanded = expandedGh === m.gh;
                const empEntry = empCache[m.gh];
                return (
                  <React.Fragment key={m.gh}>
                  <tr
                    className={`hover:bg-slate-50/50 cursor-pointer ${isDrop ? "bg-rose-50/40" : ""} ${isExpanded ? "bg-slate-50" : ""}`}
                    onClick={() => toggleGh(m.gh)}
                    data-testid={`month-mol-row-${m.gh}`}
                    title="Clic para ver el reparto por empleado en este mes"
                  >
                    <td className="p-3 font-medium text-slate-900 max-w-[280px] truncate flex items-center gap-1.5" title={m.gh}>
                      {isExpanded ? <ChevronDown className="h-3 w-3 text-slate-400 shrink-0" /> : <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />}
                      <span className="truncate">{m.gh}</span>
                    </td>
                    <td className="p-3 text-right font-mono-num text-slate-600">{fmtNum2(m.pvl)} €</td>
                    <td className="p-3 text-right font-mono-num font-semibold">{m.rate} €</td>
                    <td className="p-3 text-right font-mono-num text-slate-500">{fmtNum2(m.baseline_marca_u_pm)}</td>
                    <td className="p-3 text-right font-mono-num text-slate-500">{fmtNum2(m.baseline_gen_u_pm)}</td>
                    <td className="p-3 text-right font-mono-num font-semibold">{fmtNum2(m.month_gen_u_pm)}</td>
                    <td className="p-3 text-right font-mono-num" title={`Volumen total = marca + genérico. Mes: ${fmtNum2(monthTotal)} u/mes · Media de los ${refMonths} ${refMonths === 1 ? "mes" : "meses"} anteriores: ${fmtNum2(baseTotal)} u/mes`}>
                      <div className={`font-semibold ${isDrop ? "text-rose-700" : isLightDrop ? "text-amber-700" : varPct > 0 ? "text-emerald-700" : "text-slate-500"}`}>
                        {fmtNum2(monthTotal)} <span className="text-slate-400 font-normal">/ {baseTotal > 0 ? fmtNum2(baseTotal) : "—"}</span>
                      </div>
                      <div className={`text-[10px] ${isDrop ? "text-rose-600 font-bold" : isLightDrop ? "text-amber-600" : varPct > 0 ? "text-emerald-600" : "text-slate-400"}`}>
                        {baseTotal > 0 ? `${varPct >= 0 ? "+" : ""}${varPct.toFixed(1)}%` : "sin meses previos"}
                      </div>
                    </td>
                    <td className={`p-3 text-right font-mono-num font-semibold ${m.growth_units > 0 ? "text-emerald-700" : m.growth_units < 0 ? "text-rose-700" : "text-slate-400"}`}>
                      {m.growth_units > 0 ? "+" : ""}{fmtNum2(m.growth_units)}
                    </td>
                    <td className={`p-3 text-right font-mono-num font-bold ${m.premio_eur > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                      {m.status === "NO_SE_COBRA" ? "—" : fmtEur(m.premio_eur)}
                    </td>
                    <td className="p-3"><StatusPill status={m.status} title={reasonNoCobra} /></td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-violet-50/50" data-testid={`month-mol-employees-${m.gh}`}>
                      <td colSpan={10} className="px-4 py-3 border-t border-violet-200">
                        <MoleculeEmployeeBreakdown entry={empEntry} rate={m.rate} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              {month.molecules.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-slate-400 text-xs">Sin moléculas tracked</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MoleculeEmployeeBreakdown({ entry, rate }) {
  const sort = useTableSort(entry?.data?.by_employee || [], { pct: (e) => Number(e.pct_change || 0) });
  if (!entry || entry.loading) {
    return (
      <div className="text-slate-500 flex items-center gap-2 text-xs" data-testid="mol-emp-loading">
        <Loader2 className="h-3 w-3 animate-spin" /> Cargando reparto por empleado…
      </div>
    );
  }
  if (entry.error) {
    return <div className="text-rose-600 text-xs">{entry.error}</div>;
  }
  const data = entry.data || {};
  if (!data.has_breakdown || !(data.by_employee || []).length) {
    return (
      <div className="text-slate-500 text-xs italic" data-testid="mol-emp-empty">
        Este estudio no tiene desglose por empleado para esta molécula (o volumen 0).
      </div>
    );
  }
  const barColor = (pct) => {
    if (pct >= 70) return "bg-emerald-500";
    if (pct >= 40) return "bg-amber-500";
    return "bg-rose-500";
  };
  return (
    <div className="space-y-1.5" data-testid="mol-emp-breakdown">
      <div className="text-[10px] uppercase tracking-wider text-violet-700 font-bold flex items-center gap-1.5 mb-2">
        <Users className="h-3 w-3" /> Reparto por empleado en este mes ·
        <span className="text-slate-500 font-normal normal-case">
          Total: {data.total_marca} marca · {data.total_generic} gen · % cambio conjunto {data.pct_change_avg}%
        </span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[9px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <SortTh field="name" sort={sort} className="py-1.5 font-semibold">Empleado</SortTh>
            <SortTh field="units_marca" sort={sort} align="right" className="py-1.5 font-semibold">Marca</SortTh>
            <SortTh field="units_generic" sort={sort} align="right" className="py-1.5 font-semibold">Gen</SortTh>
            <SortTh field="opportunities" sort={sort} align="right" className="py-1.5 font-semibold">Oportunidades</SortTh>
            <SortTh field="pct" sort={sort} className="py-1.5 font-semibold px-2">% Cambio</SortTh>
          </tr>
        </thead>
        <tbody>
          {sort.sorted.map((e) => {
            const pct = Number(e.pct_change || 0);
            return (
              <tr key={e.employee_id} className="border-b border-slate-100 last:border-0">
                <td className="py-1.5 font-medium text-slate-800">{e.name}</td>
                <td className="py-1.5 text-right tabular-nums text-rose-700">{e.units_marca}</td>
                <td className="py-1.5 text-right tabular-nums text-emerald-700 font-semibold">{e.units_generic}</td>
                <td className="py-1.5 text-right tabular-nums text-slate-600">{e.opportunities}</td>
                <td className="py-1.5 px-2 min-w-[160px]">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${barColor(pct)}`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <span className="tabular-nums font-mono-num font-bold text-slate-800 w-12 text-right text-[11px]">{pct.toFixed(1)}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MoleculePicker({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);

  const search = async (term) => {
    setLoading(true);
    try {
      const r = await api.get("/incentives/molecules/search", { params: { q: term || "" } });
      setResults(r.data?.results || []);
    } catch (_) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (results.length === 0) search("");
  };

  const handleAdd = async (gh) => {
    setAdding(gh);
    await onAdd(gh);
    setAdding(null);
    setResults((rs) => rs.filter((r) => r.gh !== gh));
  };

  if (!open) {
    return (
      <button
        data-testid="open-molecule-picker"
        onClick={handleOpen}
        className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-1.5 rounded-md text-xs font-bold hover:bg-emerald-700"
        title="Añadir una molécula al seguimiento"
      >
        <Plus className="h-3.5 w-3.5" /> Añadir molécula
      </button>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-3" data-testid="molecule-picker">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Search className="h-4 w-4 text-slate-500 shrink-0" />
          <input
            data-testid="molecule-search-input"
            autoFocus
            value={q}
            onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
            placeholder="Buscar por molécula (ej. 'micofenolato'), nombre comercial o CN…"
            className="flex-1 px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
          />
        </div>
        <button
          onClick={() => { setOpen(false); setResults([]); setQ(""); }}
          className="p-1.5 text-slate-400 hover:text-slate-900"
          data-testid="close-molecule-picker"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="bg-white rounded-md border border-slate-200 max-h-[280px] overflow-y-auto">
        {loading && (
          <div className="p-4 text-center text-xs text-slate-500 inline-flex items-center justify-center gap-1.5 w-full">
            <Loader2 className="h-3 w-3 animate-spin" /> Buscando…
          </div>
        )}
        {!loading && results.length === 0 && (
          <div className="p-4 text-center text-xs text-slate-400">
            {q ? `Sin resultados para “${q}”.` : "Escribe para buscar cualquier molécula del catálogo (por nombre o CN)."}
          </div>
        )}
        {!loading && results.map((r) => (
          <div key={r.gh} className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-100 hover:bg-slate-50">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate" title={r.gh}>
                {r.gh}
                {r.is_excluded && <span className="ml-2 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded uppercase tracking-wider font-bold">excluida</span>}
                {r.in_baseline === false && <span className="ml-2 text-[10px] text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded uppercase tracking-wider font-bold">del catálogo</span>}
              </div>
              <div className="text-[11px] text-slate-500 font-mono-num">
                {r.in_baseline === false
                  ? `Sin movimiento en el estudio base · PVL ${fmtNum2(r.pvl)} €${r.has_generic ? " · con genérico disponible" : ""}`
                  : `${fmtEur(r.marca_eur_pm)} marca/mes · PVL ${fmtNum2(r.pvl)} € · ${r.marca_units} u marca / ${r.generico_units} u gen`}
              </div>
            </div>
            <button
              data-testid={`add-mol-${r.gh}`}
              onClick={() => handleAdd(r.gh)}
              disabled={adding === r.gh}
              className="inline-flex items-center gap-1 bg-slate-900 text-white px-2.5 py-1 rounded text-[11px] font-bold hover:bg-slate-700 disabled:opacity-50"
            >
              {adding === r.gh ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Añadir
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExcludedMoleculesPanel({ excludedGhs, onReinclude }) {
  if (!excludedGhs || excludedGhs.length === 0) return null;
  return (
    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="excluded-molecules-panel">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-900 mb-2 flex items-center gap-1.5">
        <Trash2 className="h-3 w-3" /> Moléculas excluidas ({excludedGhs.length})
      </div>
      <div className="flex flex-wrap gap-1.5">
        {excludedGhs.map((gh) => (
          <button
            key={gh}
            data-testid={`reinclude-${gh}`}
            onClick={() => onReinclude(gh)}
            className="inline-flex items-center gap-1 bg-white text-amber-900 hover:bg-amber-100 border border-amber-300 px-2 py-1 rounded-full text-[10px] font-medium max-w-[280px]"
            title={`Restaurar: ${gh}`}
          >
            <RotateCcw className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{gh}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function IncentivesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [savingBaseline, setSavingBaseline] = useState(false);
  const [prorate, setProrate] = useState(false);
  const [activeTab, setActiveTab] = useState("plan"); // "plan" | "six_month"
  const [sixMonth, setSixMonth] = useState(null);
  const [sixMonthLoading, setSixMonthLoading] = useState(false);
  const trackedSort = useTableSort(data?.tracked_molecules || []);

  const load = () => {
    setLoading(true);
    api.get("/incentives", { params: { prorate } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, [prorate]);

  useEffect(() => {
    if (activeTab !== "six_month") return;
    setSixMonthLoading(true);
    api.get("/incentives/six-month-benefit")
      .then((r) => setSixMonth(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Error al cargar seguimiento"))
      .finally(() => setSixMonthLoading(false));
  }, [activeTab, data]);

  const lockBaseline = async (disp_id, lock) => {
    setSavingBaseline(true);
    try {
      await api.put("/incentives/config", { baseline_disp_id: lock ? disp_id : null });
      toast.success(lock ? "Baseline fijado" : "Baseline auto-detectado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSavingBaseline(false);
    }
  };

  const excludeMolecule = async (gh) => {
    if (!window.confirm(`¿Eliminar "${gh}" del seguimiento? Podrás devolverla más tarde.`)) return;
    try {
      await api.post("/incentives/molecules/exclude", { gh });
      toast.success("Molécula eliminada del seguimiento");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al excluir");
    }
  };

  const reincludeMolecule = async (gh) => {
    try {
      await api.post("/incentives/molecules/reinclude", { gh });
      toast.success("Molécula restaurada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al restaurar");
    }
  };

  const addMolecule = async (gh) => {
    try {
      await api.post("/incentives/molecules/add", { gh });
      toast.success("Molécula añadida al seguimiento");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al añadir");
    }
  };

  const importTopVolume = async () => {
    if (!window.confirm("Se añadirán al seguimiento las top 25 moléculas por VOLUMEN (unidades marca) del baseline que aún no estén incluidas. ¿Continuar?")) return;
    try {
      const { data } = await api.post("/incentives/molecules/import-top-volume", { n: 25 });
      if ((data.added_count || 0) === 0) {
        toast.info(`Sin cambios: las 25 con más volumen ya estaban en seguimiento (${data.skipped_already_tracked} descartadas por duplicado).`);
      } else {
        toast.success(`${data.added_count} moléculas añadidas al seguimiento por volumen`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar el top por volumen");
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-slate-500 text-sm">Calculando incentivos…</div>;
  }

  if (!data || !data.baseline) {
    return (
      <div className="p-8 lg:p-12 max-w-[1400px]" data-testid="incentives-page">
        <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950 mb-3">Plan de incentivos</h1>
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">
          Sube al menos una dispensación para construir el plan de incentivos.
        </div>
      </div>
    );
  }

  const { baseline, rates, tracked_molecules: tracked, months } = data;
  const totalAcc = months.filter((m) => !m.is_baseline).reduce((s, m) => s + (m.total_premio_eur || 0), 0);

  return (
    <div className="p-8 lg:p-12 max-w-[1500px]" data-testid="incentives-page">
      {/* Hero · vídeo */}
      <div className="relative mb-10 rounded-2xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200" data-testid="incentives-hero-video">
        <video
          src="/video/farmaclass-incentives.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-auto max-h-[380px] object-cover block"
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/70 via-slate-950/30 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-8 lg:px-12 max-w-2xl pointer-events-none">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300 mb-2">FarmaClass · Incentivos</div>
          <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-white leading-tight">
            Cuando tu equipo gana, tu farmacia crece.
          </h2>
          <p className="text-sm text-white/70 mt-2 max-w-lg">
            Premia a quien más empuja la dispensación genérica y multiplica el margen mes a mes.
          </p>
        </div>
      </div>

      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Programa</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">Plan de incentivos</h1>
          <p className="text-slate-500 mt-2 max-w-2xl text-sm">
            Premia el cambio de marca a genérico mes a mes. El sistema detecta automáticamente las moléculas con mayor gasto en marca y mide el crecimiento del genérico contra el mes de referencia.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => downloadXlsx(`/incentives/export?format=xlsx&prorate=${prorate}`, "incentivos.xlsx")}
            data-testid="export-incentives-xlsx"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-900 px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-50"
          >
            <Download className="h-3 w-3" />
            Excel
          </button>
          <button
            onClick={() => downloadXlsx(`/incentives/export?format=pdf&prorate=${prorate}`, "incentivos.pdf")}
            data-testid="export-incentives-pdf"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-900 px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-50"
          >
            <FileText className="h-3 w-3" />
            PDF completo
          </button>
          <button
            onClick={() => downloadXlsx(`/incentives/molecules/export-pdf?prorate=${prorate}`, "moleculas_para_incentivar.pdf")}
            data-testid="export-molecules-pdf"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-800"
            title="PDF imprimible con el listado de moléculas para incentivar"
          >
            <FileText className="h-3 w-3" />
            PDF moléculas
          </button>
          <button
            onClick={() => downloadXlsx(`/incentives/results/export-pdf?prorate=${prorate}`, "listado_incentivos.pdf")}
            data-testid="export-results-pdf"
            className="inline-flex items-center gap-1.5 bg-emerald-700 text-white px-3 py-2 rounded-md text-xs font-medium hover:bg-emerald-800"
            title="PDF imprimible con resultados mes a mes + media farmacia"
          >
            <FileText className="h-3 w-3" />
            PDF incentivos
          </button>
          <button
            onClick={() => downloadXlsx(`/incentives/by-indication/export?prorate=${prorate}`, "incentivos_por_indicacion.xlsx")}
            data-testid="export-by-indication-xlsx"
            className="inline-flex items-center gap-1.5 bg-violet-600 text-white px-3 py-2 rounded-md text-xs font-medium hover:bg-violet-700"
            title="Excel de incentivos agrupado por indicación terapéutica y empleado"
          >
            <Download className="h-3 w-3" />
            Excel por indicación
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6" data-testid="incentives-tabs">
        <button
          onClick={() => setActiveTab("plan")}
          data-testid="tab-plan"
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === "plan" ? "border-slate-950 text-slate-950" : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          Plan actual
        </button>
        <button
          onClick={() => setActiveTab("six_month")}
          data-testid="tab-six-month"
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === "six_month" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Trophy className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
          Seguimiento 6 meses
        </button>
      </div>

      {activeTab === "six_month" && (
        <SixMonthTrackingPanel
          data={sixMonth}
          loading={sixMonthLoading}
          onBaselineChange={async (dispId) => {
            try {
              await api.put("/incentives/config", { baseline_disp_id: dispId });
              toast.success("Mes base actualizado");
              // recarga ambas vistas
              setSixMonthLoading(true);
              const r = await api.get("/incentives/six-month-benefit");
              setSixMonth(r.data);
              setSixMonthLoading(false);
              load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Error al actualizar el mes base");
            }
          }}
        />
      )}
      {activeTab === "plan" && (<>
      {editing && (
        <RatesEditor
          initial={rates}
          onSave={() => { setEditing(false); load(); }}
          onClose={() => setEditing(false)}
        />
      )}

      {data.any_partial && (
        <div className="mb-6 bg-amber-50 border border-amber-300 rounded-2xl p-4 flex items-start gap-3" data-testid="partial-month-warning">
          <span className="text-amber-600 text-lg leading-none mt-0.5">⚠</span>
          <div className="flex-1">
            <div className="font-semibold text-amber-900 text-sm">Algún estudio no cubre un mes entero — los resultados pueden no ser comparables</div>
            <div className="text-xs text-amber-900/80 mt-1">
              Las penalizaciones (NO SE COBRA) comparan volumen total mes a mes. Si un periodo cubre menos días, su volumen siempre saldrá menor.
              {prorate
                ? " Estás prorrateando los datos: cada estudio se divide entre los días reales / 30 para que los números sean equiparables."
                : " Activa el prorrateo para que los cálculos usen días reales / 30 en lugar de los meses declarados."}
            </div>
            <button
              onClick={() => setProrate(!prorate)}
              data-testid="toggle-prorate"
              className={`mt-3 inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${prorate ? "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700" : "bg-white text-amber-900 border-amber-400 hover:bg-amber-100"}`}
            >
              {prorate ? "Prorrateo activo · clic para desactivar" : "Prorratear los datos"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="baseline-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-2">
              <Info className="h-3 w-3" /> Mes de referencia
            </div>
            {baseline.is_locked ? (
              <button
                onClick={() => lockBaseline(baseline.id, false)}
                disabled={savingBaseline}
                data-testid="unlock-baseline"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-900 text-white rounded-full font-bold disabled:opacity-50"
                title="Desbloquear baseline"
              >
                <Lock className="h-2.5 w-2.5" /> Fijado
              </button>
            ) : (
              <button
                onClick={() => lockBaseline(baseline.id, true)}
                disabled={savingBaseline}
                data-testid="lock-baseline"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-bold hover:bg-slate-200 disabled:opacity-50"
                title="Fijar este mes como baseline"
              >
                <Unlock className="h-2.5 w-2.5" /> Auto
              </button>
            )}
          </div>
          <div className="font-semibold text-slate-900">{baseline.label}</div>
          <div className="text-xs text-slate-500 font-mono-num mt-1">
            {baseline.period_start} → {baseline.period_end} · {baseline.months} mes(es)
          </div>
          <div className="mt-3 text-xs text-slate-500">
            {tracked.length} moléculas en seguimiento (top {tracked.length} por € marca, PVL ≥ {rates.tier1_min} €).
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-lg p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-emerald-100 font-semibold mb-2">
            <Trophy className="h-3 w-3" /> Acumulado
          </div>
          <div className="font-display text-4xl font-bold tracking-tighter font-mono-num">{fmtEur(totalAcc)}</div>
          <div className="text-xs text-emerald-100 mt-1">premio total en {months.length - 1} mes(es) post-baseline</div>
        </div>

        <RatesCard rates={rates} onEdit={() => setEditing(true)} />
      </div>

      <div className="space-y-3" data-testid="incentives-months">
        {months.map((m) => (
          <MonthCard
            key={m.id}
            month={m}
            tracked={tracked}
            rates={rates}
            isLockedBaseline={baseline.is_locked && m.id === baseline.id}
            onLockBaseline={() => lockBaseline(m.id, true)}
          />
        ))}
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="tracked-molecules-summary">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-display text-base font-bold">Moléculas en seguimiento ({tracked.length}) · baseline</div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={importTopVolume}
              data-testid="import-top25-volume"
              title="Añadir las 25 moléculas con más unidades de marca del baseline"
              className="inline-flex items-center gap-1.5 bg-sky-600 text-white px-3 py-1.5 rounded-md text-xs font-bold hover:bg-sky-700"
            >
              <Trophy className="h-3.5 w-3.5" />
              Importar TOP 25 por volumen
            </button>
            <MoleculePicker onAdd={addMolecule} />
            <button
              onClick={() => downloadXlsx(`/incentives/molecules/export-pdf?prorate=${prorate}`, "moleculas_para_incentivar.pdf")}
              data-testid="export-molecules-pdf-inline"
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-slate-800"
              title="Descargar listado imprimible"
            >
              <FileText className="h-3 w-3" />
              PDF imprimible
            </button>
          </div>
        </div>
        <ExcludedMoleculesPanel
          excludedGhs={data?.config?.excluded_ghs}
          onReinclude={reincludeMolecule}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3 font-semibold">#</th>
                <SortTh field="gh" sort={trackedSort} className="p-3 font-semibold">Molécula (G.H.)</SortTh>
                <SortTh field="pvl" sort={trackedSort} align="right" className="p-3 font-semibold">PVL conjunto</SortTh>
                <SortTh field="rate" sort={trackedSort} align="right" className="p-3 font-semibold">Tarifa</SortTh>
                <SortTh field="marca_eur_pm" sort={trackedSort} align="right" className="p-3 font-semibold">Marca €/mes (base)</SortTh>
                <SortTh field="baseline_marca_u_pm" sort={trackedSort} align="right" className="p-3 font-semibold">Marca u/mes</SortTh>
                <SortTh field="baseline_gen_u_pm" sort={trackedSort} align="right" className="p-3 font-semibold">Gen u/mes</SortTh>
                <th className="text-center p-3 font-semibold w-16" title="Eliminar del seguimiento">Quitar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {trackedSort.sorted.map((t, i) => (
                <tr key={t.gh} className="hover:bg-slate-50 group">
                  <td className="p-3 text-slate-400 font-mono-num">{i + 1}</td>
                  <td className="p-3 font-medium text-slate-900 max-w-[320px] truncate" title={t.gh}>{t.gh}</td>
                  <td className="p-3 text-right font-mono-num">{fmtNum2(t.pvl)} €</td>
                  <td className="p-3 text-right font-mono-num font-semibold">{t.rate} €</td>
                  <td className="p-3 text-right font-mono-num font-semibold">{fmtEur(t.marca_eur_pm)}</td>
                  <td className="p-3 text-right font-mono-num text-slate-500">{fmtNum2(t.baseline_marca_u_pm)}</td>
                  <td className="p-3 text-right font-mono-num text-slate-500">{fmtNum2(t.baseline_gen_u_pm)}</td>
                  <td className="p-3 text-center">
                    <button
                      data-testid={`remove-mol-${t.gh}`}
                      onClick={() => excludeMolecule(t.gh)}
                      title={`Quitar "${t.gh}" del seguimiento`}
                      className="p-1.5 text-slate-400 hover:text-rose-700 hover:bg-rose-50 rounded transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {tracked.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400 text-sm">No hay moléculas elegibles (PVL ≥ 6 € con € en marca) en el baseline.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>)}
    </div>
  );
}

function SixMonthTrackingPanel({ data, loading, onBaselineChange }) {
  const sort = useTableSort(data?.months || []);
  if (loading) {
    return (
      <div className="py-12 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Calculando beneficio a 6 meses…
      </div>
    );
  }
  if (!data || !data.baseline) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500 text-sm">
        Sube al menos dos estudios para poder comparar contra un mes base.
      </div>
    );
  }
  const { baseline, months, accumulated_eur, months_elapsed, target_months, avg_generic_margin_pct } = data;
  const progressPct = Math.min(100, (months_elapsed / target_months) * 100);
  const monthlyRate = months.length ? accumulated_eur / months.length : 0;
  const projected6m = round2(monthlyRate * target_months);

  return (
    <div className="space-y-6" data-testid="six-month-panel">
      {/* Card grande resumen */}
      <div className="bg-gradient-to-br from-emerald-50 via-white to-white border-2 border-emerald-200 rounded-2xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-1">Ganado desde el mes base</div>
            <div className="font-display text-5xl font-extrabold text-emerald-900 font-mono-num">
              {fmtEur(accumulated_eur)}
            </div>
            <div className="text-xs text-slate-600 mt-1.5">
              Mes base: <strong>{baseline.label || baseline.period_start?.slice(0,7)}</strong>
              {" · "}
              {months_elapsed}/{target_months} meses transcurridos
              {" · "}
              margen medio genérico aplicado: {avg_generic_margin_pct}%
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">Proyección 6 meses</div>
            <div className="font-display text-3xl font-bold text-slate-900 font-mono-num">
              {fmtEur(projected6m)}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Ritmo actual × 6</div>
          </div>
        </div>
        <div className="mt-5">
          <div className="h-3 bg-white rounded-full overflow-hidden border border-emerald-200">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
              style={{ width: `${progressPct}%` }}
              data-testid="six-month-progress-bar"
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            Progreso hasta reparto de la segunda fase (6 meses)
          </div>
        </div>
      </div>

      {/* Selector mes base */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
        <label className="text-xs font-semibold text-slate-600">Mes base:</label>
        <select
          value={baseline.disp_id}
          onChange={(e) => onBaselineChange(e.target.value)}
          data-testid="six-month-baseline-select"
          className="text-xs border border-slate-300 rounded-md px-3 py-1.5"
        >
          <option value={baseline.disp_id}>
            {baseline.label} — {baseline.period_start?.slice(0,7)} ({baseline.generic_pct_eur}% disp.)
          </option>
          {months.map((m) => (
            <option key={m.disp_id} value={m.disp_id}>
              {m.label} — {m.period_start?.slice(0,7)} ({m.generic_pct_eur}% disp.)
            </option>
          ))}
        </select>
        <span className="text-[11px] text-slate-500">
          Al cambiar el mes base recalcula todo el seguimiento.
        </span>
      </div>

      {/* Tabla mes a mes */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">Evolución mes a mes</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Beneficio = (Δ % dispensación) × € del mes × margen medio del genérico
          </div>
        </div>
        <table className="w-full text-xs" data-testid="six-month-months-table">
          <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wider">
            <tr>
              <SortTh field="label" sort={sort} className="px-3 py-2">Mes</SortTh>
              <SortTh field="generic_pct_eur" sort={sort} align="right" className="px-3 py-2">% Dispensación €</SortTh>
              <SortTh field="delta_pp" sort={sort} align="right" className="px-3 py-2">Δ vs. base (pp)</SortTh>
              <SortTh field="total_eur" sort={sort} align="right" className="px-3 py-2">Total € del mes</SortTh>
              <SortTh field="benefit_eur" sort={sort} align="right" className="px-3 py-2">Beneficio ganado</SortTh>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-slate-100 bg-slate-50">
              <td className="px-3 py-2 font-semibold">
                {baseline.label} <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">BASE</span>
              </td>
              <td className="px-3 py-2 text-right font-mono-num">{baseline.generic_pct_eur}%</td>
              <td className="px-3 py-2 text-right text-slate-400">—</td>
              <td className="px-3 py-2 text-right font-mono-num">{fmtEur(baseline.total_eur)}</td>
              <td className="px-3 py-2 text-right text-slate-400">—</td>
            </tr>
            {sort.sorted.map((m) => (
              <tr key={m.disp_id} className="border-t border-slate-100">
                <td className="px-3 py-2">{m.label}</td>
                <td className="px-3 py-2 text-right font-mono-num">{m.generic_pct_eur}%</td>
                <td className={`px-3 py-2 text-right font-mono-num font-semibold ${m.delta_pp > 0 ? "text-emerald-700" : m.delta_pp < 0 ? "text-rose-600" : "text-slate-400"}`}>
                  {m.delta_pp > 0 ? "+" : ""}{m.delta_pp} pp
                </td>
                <td className="px-3 py-2 text-right font-mono-num text-slate-600">{fmtEur(m.total_eur)}</td>
                <td className={`px-3 py-2 text-right font-mono-num font-bold ${m.benefit_eur > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                  {fmtEur(m.benefit_eur)}
                </td>
              </tr>
            ))}
            {months.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400">Aún no hay meses posteriores al base.</td></tr>
            )}
          </tbody>
          {months.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-emerald-50">
                <td colSpan={4} className="px-3 py-3 text-right font-bold text-emerald-800 uppercase tracking-wider text-[10px]">Total acumulado</td>
                <td className="px-3 py-3 text-right font-mono-num font-extrabold text-emerald-800 text-sm">{fmtEur(accumulated_eur)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
